import React from "react";
import { motion, AnimatePresence, type PanInfo } from "framer-motion";
import { cn } from "../lib/utils.js";

interface ImageStageProps {
  chatName: string;
  thumbURLs: string[];
  fullURLs: string[];
  activeIndex: number;
  direction: number;
  isImmersive: boolean;
  onToggleImmersive: () => void;
  onSelectIndex: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
}

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 88 : -88, opacity: 0.24, scale: 0.985 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (dir: number) => ({ x: dir < 0 ? 88 : -88, opacity: 0.2, scale: 0.985 }),
};

const SWIPE_POWER = 10_000;

function swipeConfidence(offset: number, velocity: number) {
  return Math.abs(offset) * velocity;
}

export function ImageStage({
  chatName,
  thumbURLs,
  fullURLs,
  activeIndex,
  direction,
  isImmersive,
  onToggleImmersive,
  onSelectIndex,
  onPrev,
  onNext,
}: ImageStageProps) {
  const count = thumbURLs.length || fullURLs.length;
  const currentThumb = thumbURLs[activeIndex] ?? null;
  const currentFull = fullURLs[activeIndex] ?? currentThumb;

  return (
    <section
      className={cn(
        "relative min-w-0 overflow-hidden bg-[linear-gradient(180deg,#f1ece3_0%,#ebe5db_100%)]",
        // Mobile: fixed height so it scrolls away naturally
        "h-[58vh] md:h-auto md:flex-1 md:min-h-0",
        isImmersive ? "md:basis-full" : "md:basis-[60%]"
      )}
    >
      <div className="absolute inset-y-0 left-0 w-[16%] bg-[radial-gradient(circle_at_left,rgba(255,255,255,0.40),transparent_70%)] pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-[22%] bg-[linear-gradient(270deg,rgba(229,222,212,0.55),transparent)] pointer-events-none" />

      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <div className="flex flex-1 min-h-0 items-center justify-center px-2 py-2 md:px-4 md:py-4 lg:px-5">
          <div className="relative flex h-full w-full items-center justify-center">
            <div className="relative flex h-full w-full items-center justify-center">
              <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.button
                  key={activeIndex}
                  type="button"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
                  drag={count > 1 ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.12}
                  onDragEnd={(_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
                    const swipe = swipeConfidence(info.offset.x, info.velocity.x);
                    if (swipe < -SWIPE_POWER) onNext();
                    if (swipe > SWIPE_POWER) onPrev();
                  }}
                  onClick={onToggleImmersive}
                  className={cn(
                    "group relative flex w-full items-center justify-center overflow-hidden rounded-[28px]",
                    "border border-black/6 bg-[rgba(255,252,247,0.76)] shadow-[0_20px_70px_rgba(44,38,29,0.10)]",
                    "cursor-zoom-in backdrop-blur-[2px]"
                  )}
                  style={{ height: "100%" }}
                  aria-label={isImmersive ? "Hiện lại bảng thông tin" : "Ẩn bảng thông tin để xem ảnh lớn hơn"}
                >
                  {currentFull ? (
                    <img
                      src={currentFull}
                      alt={`${chatName} - ${activeIndex + 1}`}
                      className="block h-[calc(100%-1.25rem)] w-auto max-w-full object-contain select-none"
                      draggable={false}
                    />
                  ) : (
                    <div className="flex h-full min-h-[44vh] w-full items-center justify-center text-sm text-ink-2">
                      Không có preview
                    </div>
                  )}

                  <div className="absolute bottom-3 left-3 rounded-full bg-white/78 px-3 py-1 text-[11px] font-medium text-ink-1 shadow-sm">
                    {activeIndex + 1} / {Math.max(count, 1)}
                  </div>

                  <div className="absolute right-3 top-3 rounded-full bg-white/72 px-3 py-1 text-[11px] font-medium text-ink-2 shadow-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    {isImmersive ? "Hiện thông tin" : "Tập trung vào ảnh"}
                  </div>
                </motion.button>
              </AnimatePresence>

              {count > 1 && (
                <>
                  <StageNavButton direction="prev" onClick={onPrev} disabled={activeIndex === 0} />
                  <StageNavButton direction="next" onClick={onNext} disabled={activeIndex === count - 1} />
                </>
              )}
            </div>
          </div>
        </div>

        {count > 1 && (
          <div className="px-2 pb-2 md:px-4 md:pb-3 lg:px-5">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide rounded-full bg-white/54 px-3 py-2 shadow-[inset_0_0_0_1px_rgba(26,25,23,0.05)] backdrop-blur-sm">
              {thumbURLs.map((url, index) => {
                const active = index === activeIndex;
                return (
                  <button
                    key={index}
                    onClick={() => onSelectIndex(index)}
                    className={cn(
                      "relative flex-shrink-0 overflow-hidden rounded-2xl transition-all duration-200",
                      active ? "opacity-100 ring-2 ring-ink-0/65 shadow-md" : "opacity-50 hover:opacity-80"
                    )}
                    style={{ width: 42, aspectRatio: "3 / 4" }}
                    aria-label={`Xem ảnh ${index + 1}`}
                  >
                    <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isImmersive && currentFull && (
          <>
            <motion.div
              key="immersive-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-[60] bg-[rgba(16,15,13,0.96)]"
              onClick={onToggleImmersive}
            />
            <motion.div
              key="immersive-content"
              initial={{ opacity: 0, scale: 0.985 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.985 }}
              transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="fixed inset-0 z-[61] flex items-center justify-center px-4 py-4 md:px-8"
              onClick={onToggleImmersive}
            >
              <img
                src={currentFull}
                alt={chatName}
                className="max-h-[92vh] max-w-full rounded-[24px] object-contain shadow-[0_20px_90px_rgba(0,0,0,0.35)]"
                draggable={false}
                onClick={(event) => event.stopPropagation()}
              />

              <button
                onClick={(e) => { e.stopPropagation(); onToggleImmersive(); }}
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-black/65 text-white transition-colors hover:bg-black/82"
                aria-label="Đóng chế độ xem ảnh lớn"
              >
                <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <path d="M2 2l10 10M12 2L2 12" />
                </svg>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}

function StageNavButton({
  direction,
  onClick,
  disabled,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "absolute top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full",
        "border border-black/8 bg-white/82 text-ink-1 shadow-[0_10px_30px_rgba(30,25,18,0.10)] backdrop-blur-sm transition-all duration-200",
        "hover:bg-white disabled:cursor-not-allowed disabled:opacity-20 md:flex",
        direction === "prev" ? "left-4 lg:left-6" : "right-4 lg:right-6"
      )}
      aria-label={direction === "prev" ? "Ảnh trước" : "Ảnh tiếp theo"}
    >
      {direction === "prev" ? (
        <svg width={15} height={15} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
          <path d="M9 2.5L4.5 7 9 11.5" />
        </svg>
      ) : (
        <svg width={15} height={15} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
          <path d="M5 2.5L9.5 7 5 11.5" />
        </svg>
      )}
    </button>
  );
}
